import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import clsx from 'clsx';
import UseInput from '../../hooks/useInput';
import { useContactForm } from '../../hooks/useContactForm';
import { validateContactEmail, validateNotEmpty, validateEmailBusiness } from './ValidationLogic';
import Swal from 'sweetalert2';
import SharedButton from '@/global/Button/Button';
import Loader from '@/components/Common/Loader';
import BackgroundPages from '@/global/Background/BackgroundPages';
import useText from '@/hooks/useText';
import { supportFindUs, supportFormSection, supportHelpCenter } from '@/data/supportContent';
import {
  buildContactAttachments,
  CONTACT_ATTACHMENT_ACCEPT,
  CONTACT_ATTACHMENT_MAX_COUNT,
  type ContactAttachmentPreview,
  validateContactAttachmentSelection,
} from './contactAttachments';
import '@/pages/partners.css';
import './support.css';

const FIELD_LABEL = 'block text-sm font-medium text-slate-700 dark:text-zinc-300';
const FIELD_ERROR = 'mt-1 text-xs text-red-600 dark:text-red-300';

function fieldInputClass(isInvalid: boolean) {
  return clsx('support-field-input', isInvalid && 'support-field-input--invalid');
}

export default function SupportContent() {
  const [isPolicyAccepted, setIsPolicyAccepted] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [formStartedAt, setFormStartedAt] = useState(() => Date.now());
  const [attachments, setAttachments] = useState<ContactAttachmentPreview[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const support = useText('home.footer.support');

  const { isSubmitting, emailSentMessage, formError, setFormError, submitMessage, resetForm } =
    useContactForm();

  const {
    value: name,
    isInputValid: isNameValid,
    isInputInvalid: isNameInvalid,
    valueChangeHandler: nameChangeHandler,
    valueBlurHandler: nameBlurHandler,
    resetInput: resetNameInput,
  } = UseInput(validateNotEmpty);

  const {
    value: email,
    isInputValid: isEmailValid,
    isInputInvalid: isEmailInvalid,
    valueChangeHandler: emailChangeHandler,
    valueBlurHandler: emailBlurHandler,
    resetInput: resetEmailInput,
  } = UseInput(validateContactEmail);

  const {
    value: company,
    isInputValid: isCompanyValid,
    isInputInvalid: isCompanyInvalid,
    valueChangeHandler: companyChangeHandler,
    valueBlurHandler: companyBlurHandler,
    resetInput: resetCompanyInput,
  } = UseInput(validateNotEmpty);

  const {
    value: message,
    isInputValid: isMessageValid,
    isInputInvalid: isMessageInvalid,
    valueChangeHandler: messageChangeHandler,
    valueBlurHandler: messageBlurHandler,
    resetInput: resetMessageInput,
  } = UseInput(validateNotEmpty);

  const ACADEMIC_SUBJECT = 'I would like to apply for an academic license';
  const REQUEST_PRICING_SUBJECT = 'Request pricing';

  const isNonInstitutionalRequestPricing =
    selectedSubject === REQUEST_PRICING_SUBJECT &&
    email.trim() !== '' &&
    !validateEmailBusiness(email.trim().toLowerCase());

  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach(item => URL.revokeObjectURL(item.previewUrl));
    };
  }, []);

  function clearAttachments() {
    attachments.forEach(item => URL.revokeObjectURL(item.previewUrl));
    setAttachments([]);
    setAttachmentError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function resetFormHandler() {
    resetNameInput();
    resetEmailInput();
    resetCompanyInput();
    resetMessageInput();
    setIsPolicyAccepted(false);
    resetForm();
    setSelectedSubject('');
    setIsPopupVisible(false);
    setFormStartedAt(Date.now());
    clearAttachments();
  }

  const handleGoToPrivacyPolicy = () => {
    window.open('/privacy-policy', '_blank');
  };

  function checkFormValidity() {
    const inputsValidity = [isNameValid, isEmailValid, isCompanyValid, isMessageValid];
    return (
      inputsValidity.every(isInputValid => isInputValid) &&
      selectedSubject !== ACADEMIC_SUBJECT &&
      !isNonInstitutionalRequestPricing
    );
  }

  const handleDisableSubmitButton = () => {
    return (
      !isPolicyAccepted ||
      selectedSubject === ACADEMIC_SUBJECT ||
      isNonInstitutionalRequestPricing ||
      !checkFormValidity()
    );
  };

  function handleAttachmentChange(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = Array.from(event.target.files ?? []);
    if (!selectedFiles.length) {
      return;
    }

    const validationError = validateContactAttachmentSelection(attachments.length, selectedFiles);
    if (validationError) {
      setAttachmentError(validationError);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setAttachmentError(null);
    const nextAttachments = selectedFiles.map(file => ({
      id: `${file.name}-${file.lastModified}-${crypto.randomUUID()}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setAttachments(prev => [...prev, ...nextAttachments].slice(0, CONTACT_ATTACHMENT_MAX_COUNT));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function removeAttachment(id: string) {
    setAttachments(prev => {
      const removed = prev.find(item => item.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return prev.filter(item => item.id !== id);
    });
    setAttachmentError(null);
  }

  async function submitMessageHandler(event: React.FormEvent) {
    event.preventDefault();
    const messageInfo = {
      name: name,
      email: email,
      organization: company,
      message: message,
      subject: selectedSubject,
      startedAt: formStartedAt,
      website: '',
      attachments:
        attachments.length > 0
          ? await buildContactAttachments(attachments.map(item => item.file))
          : undefined,
    };
    if (checkFormValidity()) {
      const success = await submitMessage(messageInfo);
      if (success) {
        Swal.fire({
          title: 'Form sent successfully',
          text: 'We will get back to you shortly.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3B82F6',
        });
        resetFormHandler();
      }
    } else {
      setFormError('Validation failed. Check your inputs and try again');
    }
  }

  const debouncedSubmitHandler = debounce(
    (event: React.FormEvent) => {
      event.preventDefault();
      submitMessageHandler(event);
    },
    500,
    { leading: true, trailing: false },
  );

  function onChangePolicyHandler() {
    setIsPolicyAccepted(prevState => !prevState);
  }

  function handleSubjectChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value;
    setSelectedSubject(value);
    setIsPopupVisible(value === ACADEMIC_SUBJECT);
  }

  const statusMessage = formError || emailSentMessage;
  const statusIsError = Boolean(formError);

  return (
    <>
      <BackgroundPages />
      <div className="relative">
        {isSubmitting && <Loader />}
        <section id="support-form" className="landing-section landing-surface-a scroll-mt-24 pt-2">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="support-form-glow mx-auto max-w-5xl rounded-3xl border border-slate-200/90 bg-white p-8 lg:p-10 xl:p-12 lg:mt-10">
              <div className="mx-auto max-w-3xl text-center">
                <p className="landing-eyebrow">{supportFormSection.eyebrow}</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-2xl">
                  {supportFormSection.title}
                </h2>
                <p className="mx-auto mt-3 max-w-2xl text-base text-slate-200 sm:text-sm">
                  If you need assistance with licensing options, upgrading your account, or have other
                  inquiries, please feel free to fill out the form below.
                </p>
              </div>

              <div className="mx-auto mt-10 max-w-5xl">
                <form className="space-y-4" onSubmit={debouncedSubmitHandler}>
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="pointer-events-none absolute left-[-9999px] top-[-9999px] h-0 w-0 opacity-0"
                  />

                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="support-name" className={FIELD_LABEL}>
                          Name *
                        </label>
                        <input
                          id="support-name"
                          className={fieldInputClass(isNameInvalid)}
                          type="text"
                          name="name"
                          placeholder="Name"
                          onChange={nameChangeHandler}
                          onBlur={nameBlurHandler}
                          value={name}
                        />
                        {isNameInvalid && <p className={FIELD_ERROR}>Please enter your name</p>}
                      </div>

                      <div>
                        <label htmlFor="support-company" className={FIELD_LABEL}>
                          Company / Organization *
                        </label>
                        <input
                          id="support-company"
                          className={fieldInputClass(isCompanyInvalid)}
                          type="text"
                          name="company"
                          placeholder="Company / Organization"
                          onChange={companyChangeHandler}
                          onBlur={companyBlurHandler}
                          value={company}
                        />
                        {isCompanyInvalid && (
                          <p className={FIELD_ERROR}>Please enter your company&apos;s name</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="support-email" className={FIELD_LABEL}>
                          Email *
                        </label>
                        <input
                          id="support-email"
                          className={fieldInputClass(isEmailInvalid)}
                          type="email"
                          name="email"
                          placeholder="Email"
                          onChange={emailChangeHandler}
                          onBlur={emailBlurHandler}
                          value={email}
                        />
                        {isEmailInvalid && (
                          <p className={FIELD_ERROR}>Please enter a valid email: name@example.com</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="support-subject" className={FIELD_LABEL}>
                          Subject *
                        </label>
                        <select
                          id="support-subject"
                          className={fieldInputClass(false)}
                          name="subject"
                          onChange={handleSubjectChange}
                          value={selectedSubject}
                        >
                          <option value="">Select a subject</option>
                          <option value={ACADEMIC_SUBJECT}>{ACADEMIC_SUBJECT}</option>
                          <option value="Request pricing">Request pricing</option>
                          <option value="I would like more information about the product or license">
                            I would like more information about the product or license
                          </option>
                          <option value="I would like to contact the support team">
                            I would like to contact the support team
                          </option>
                          <option value="I want to renew my account/My account is disabled">
                            I want to renew my account/My account is disabled
                          </option>
                          <option value="Other">Other</option>
                        </select>
                        {isNonInstitutionalRequestPricing && (
                          <p className={FIELD_ERROR}>
                            Only users with an institutional email can submit a request with this subject.
                            Please select a different option.
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="support-message" className={FIELD_LABEL}>
                        Message *
                      </label>
                      <textarea
                        id="support-message"
                        className={clsx(
                          'support-field-textarea',
                          isMessageInvalid && 'support-field-input--invalid',
                        )}
                        name="message"
                        rows={5}
                        placeholder="Questions / comments"
                        onChange={messageChangeHandler}
                        onBlur={messageBlurHandler}
                        value={message}
                      />
                      {isMessageInvalid && (
                        <p className={FIELD_ERROR}>Please enter your question / comments</p>
                      )}

                      <div className="support-attachments">
                        <p className={FIELD_LABEL}>Screenshots / images (optional)</p>
                        <p className="support-attachments__hint">
                          PNG, JPG, GIF or WEBP. Up to {CONTACT_ATTACHMENT_MAX_COUNT} files, 5 MB each.
                        </p>
                        <div className="support-attachments__picker">
                          <input
                            ref={fileInputRef}
                            id="support-attachments"
                            type="file"
                            accept={CONTACT_ATTACHMENT_ACCEPT}
                            multiple
                            className="support-attachments__input"
                            onChange={handleAttachmentChange}
                            disabled={attachments.length >= CONTACT_ATTACHMENT_MAX_COUNT}
                          />
                          <label
                            htmlFor="support-attachments"
                            className={clsx(
                              'support-attachments__button',
                              attachments.length >= CONTACT_ATTACHMENT_MAX_COUNT &&
                                'support-attachments__button--disabled',
                            )}
                          >
                            Choose files
                          </label>
                          <span className="support-attachments__status" aria-live="polite">
                            {attachments.length === 0
                              ? 'No files selected'
                              : attachments.length === 1
                                ? '1 file selected'
                                : `${attachments.length} files selected`}
                          </span>
                        </div>
                        {attachmentError && <p className={FIELD_ERROR}>{attachmentError}</p>}
                        {attachments.length > 0 && (
                          <ul className="support-attachments__list" aria-label="Selected images">
                            {attachments.map(item => (
                              <li key={item.id} className="support-attachments__item">
                                <img
                                  src={item.previewUrl}
                                  alt={item.file.name}
                                  className="support-attachments__thumb"
                                />
                                <button
                                  type="button"
                                  className="support-attachments__remove"
                                  aria-label={`Remove ${item.file.name}`}
                                  onClick={() => removeAttachment(item.id)}
                                >
                                  ×
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 pt-2">
                    <input
                      type="checkbox"
                      id="support-policy"
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-pink focus:ring-pink/40"
                      name="academicLicenseCheckbox"
                      checked={isPolicyAccepted}
                      onChange={onChangePolicyHandler}
                    />
                    <label htmlFor="support-policy" className="text-sm text-slate-600 dark:text-slate-400">
                      I have read and accept{' '}
                      <button
                        type="button"
                        onClick={handleGoToPrivacyPolicy}
                        className="font-semibold text-pink hover:underline"
                      >
                        DISGENET&apos;s privacy policy
                      </button>
                    </label>
                  </div>
                  <p className="mt-3  text-slate-600 text-xs">{supportFormSection.body}</p>

                  <div className="space-y-3 pt-2">
                    {statusMessage && (
                      <div
                        className={clsx(
                          'rounded-xl border px-4 py-3 text-sm',
                          statusIsError
                            ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200',
                        )}
                      >
                        {statusMessage}
                      </div>
                    )}

                    <div className="flex justify-end">
                      <SharedButton
                        type="submit"
                        variant="primary"
                        size="md"
                        className="!m-0 rounded-full px-6"
                        disabled={handleDisableSubmitButton()}
                        title={
                          selectedSubject === ACADEMIC_SUBJECT
                            ? 'Apply for an academic license on the academic apply page.'
                            : ''
                        }
                      >
                        {isSubmitting ? 'Submitting...' : 'Send'}
                      </SharedButton>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200/80 bg-white py-12 dark:border-white/10 dark:bg-[#050505] sm:py-14">
          <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
            <p className="text-base text-slate-600 dark:text-slate-400">{supportHelpCenter.body}</p>
            <div className="mt-5">
              <SharedButton
                variant="gradient"
                size="sm"
                className="!m-0"
                onClick={() => window.open(supportHelpCenter.url, '_blank', 'noopener,noreferrer')}
              >
                {supportHelpCenter.buttonLabel}
              </SharedButton>
            </div>
          </div>
        </section>

        <section className="landing-section landing-surface-b">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2 lg:items-stretch">
              <div className="flex flex-col justify-center">
                <p className="landing-eyebrow">{supportFindUs.eyebrow}</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-2xl">{supportFindUs.title}</h2>
                <p className="mt-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
                  {supportFindUs.company}
                </p>
                <address className="mt-4 not-italic text-base leading-relaxed text-slate-600 dark:text-slate-400">
                  {supportFindUs.lines.map(line => (
                    <p key={line}>{line}</p>
                  ))}
                </address>
              </div>
              <div className="min-h-[280px] overflow-hidden rounded-3xl border border-slate-200/80 shadow-sm dark:border-white/10 lg:min-h-[320px]">
                <iframe
                  title={`Map: ${supportFindUs.company}`}
                  className="h-full min-h-[280px] w-full border-0 lg:min-h-[320px]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={supportFindUs.mapEmbedSrc}
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>

        {isPopupVisible && (
          <div
            className="support-popup-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="academic-popup-title"
          >
            <div className="support-popup-panel">
              <h3 id="academic-popup-title" className="text-lg font-bold text-slate-900 dark:text-white">
                Important
              </h3>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                To apply for an academic license, please go to the{' '}
                <Link
                  to="/academic-apply"
                  className="font-semibold text-pink hover:underline"
                  onClick={() => setIsPopupVisible(false)}
                >
                  academic apply page
                </Link>
                .
              </p>
              <SharedButton
                variant="primary"
                size="sm"
                className="!m-0 mt-5"
                onClick={() => setIsPopupVisible(false)}
              >
                Close
              </SharedButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
